import React from 'react';
import PropTypes from 'prop-types';
import { compose, withStateHandlers, withHandlers } from 'recompose';
import { getStaticViewPixel } from '@sparefoot/react-core/utils/tracking';
import { StickyContainer } from '@sparefoot/react-core';

import { unitSizeOptions } from 'config/units';
import {
	unitAmenities,
	vehicleUnitSizes,
	specialUnitSizes,
} from 'config/storageBasics';
import { withSearchHandler, withCanonical } from 'decorators';
import {
	Page,
	Section,
	Container,
	Group,
	Row,
	Column,
} from 'components/core/Page';
import { H1, H2, H3, H4, Paragraph, Text } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { Button, ScrollButton } from 'components/core/Button';
import { BasicsCard } from 'components/legacy/BasicsCard';
import { SearchModal } from 'components/search/SearchModal';
import { UnitIcon } from 'components/unit/UnitIcon';

import movingOutImage from 'assets/images/storage-basics/wte--moving-out.jpg';
import movingInImage from 'assets/images/storage-basics/wte--moving-in.jpg';

import './StorageBasicsPage.scss';
import { PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import { withPageViewTracking } from 'components/core/library/tracking-utils';

export const enhance = compose(
	withCanonical('/what-to-expect'),
	withStateHandlers(
		{
			additionalSearchParams: null,
			searchModalShowing: false,
		},
		{
			onShowSearchModal: () => (additionalSearchParams) => ({
				additionalSearchParams,
				searchModalShowing: true,
			}),
			onHideSearchModal: () => () => ({
				additionalSearchParams: null,
				searchModalShowing: false,
			}),
		},
	),
	withPageViewTracking(PAGE_SUB_TYPES.STORAGE_BASICS_LANDING_PAGE),
	withHandlers({
		searchUnitsBySize:
			({ onShowSearchModal }) =>
			(sqft) =>
			() =>
				onShowSearchModal({ sqft }),
		searchUnitsByAmenity:
			({ onShowSearchModal }) =>
			(amenity) =>
			() =>
				onShowSearchModal({ amenities: [amenity] }),
		searchVehcileUnitsBySize:
			({ onShowSearchModal }) =>
			(sqft) =>
			() =>
				onShowSearchModal({ sqft, type: 'vehicle' }),
		searchVehicleUnits:
			({ onShowSearchModal }) =>
			() =>
				onShowSearchModal({ type: 'vehicle' }),
	}),
	withSearchHandler,
);

export function StorageBasicsPage({
	handleSearch,
	searchModalShowing,
	searchUnitsBySize,
	searchVehicleUnits,
	searchUnitsByAmenity,
	searchVehcileUnitsBySize,
	onHideSearchModal,
	canonical,
}) {
	return (
		<Page
			className="storage-basics"
			trackingPixel={getStaticViewPixel()}
			pageType={PAGE_SUB_TYPES.STORAGE_BASICS_LANDING_PAGE}
			title="Self-Storage Basics: What to Expect | SelfStorage.com"
			canonical={canonical}
		>
			<SearchModal
				showing={searchModalShowing}
				onClose={onHideSearchModal}
				onSearch={handleSearch}
			/>
			<StickyContainer>
				<div className="page-header">
					<Container>
						<Group>
							<H1
								size="medium"
								className="page-title"
							>
								Self-Storage Basics: What to Expect
							</H1>
						</Group>
						<ul className="page-menu">
							<li>
								<ScrollButton
									offset={-180}
									secondary
									href="#unit-sizes"
									className="page-menu-button"
								>
									Unit Size
								</ScrollButton>
							</li>
							<li>
								<ScrollButton
									offset={-180}
									secondary
									href="#vehicle-storage"
									className="page-menu-button"
								>
									Vehicle Storage
								</ScrollButton>
							</li>
							<li>
								<ScrollButton
									offset={-180}
									secondary
									href="#unit-amenities"
									className="page-menu-button"
								>
									Unit Amenities
								</ScrollButton>
							</li>
							<li>
								<ScrollButton
									offset={-180}
									secondary
									href="#moving-in"
									className="page-menu-button"
								>
									Moving In
								</ScrollButton>
							</li>
							<li>
								<ScrollButton
									offset={-180}
									secondary
									href="#moving-out"
									className="page-menu-button"
								>
									Moving Out
								</ScrollButton>
							</li>
						</ul>
					</Container>
				</div>
			</StickyContainer>

			<Section
				className="unit-sizes"
				id="unit-sizes"
			>
				<Container>
					<Group>
						<H2
							className="section-title"
							size="large"
						>
							Storage Unit Sizes
						</H2>
						<H3 className="section-title">
							Standard units will have a ceiling height of eight
							feet.
						</H3>
					</Group>
					<Row>
						{unitSizeOptions.map((unitSize) => (
							<Column
								medium={6}
								xlarge={3}
								key={unitSize.sqft}
							>
								<BasicsCard header={unitSize.title}>
									<Group className="unit-container">
										<div className="unit-image">
											<UnitIcon
												type="storage"
												squareFootage={unitSize.sqft}
												className={unitSize.className}
											/>
										</div>
										<div className="unit-size-info unit-size-card">
											<div className="tui">
												This unit is:
											</div>
											<div className="sqft">
												<Paragraph
													last
													size="large"
												>
													{unitSize.sqft}
												</Paragraph>
												<Paragraph>
													Square Feet
												</Paragraph>
											</div>
										</div>
									</Group>
									<Group className="unit-info">
										<Paragraph
											size="small"
											last
										>
											Looks like:
										</Paragraph>
										<Paragraph>
											{unitSize.looksLike}
										</Paragraph>
										<Paragraph
											size="small"
											last
										>
											What will fit:
										</Paragraph>
										<Paragraph>
											{unitSize.whatFits}
										</Paragraph>
									</Group>
									<Button
										primary
										fullWidth
										onClick={searchUnitsBySize(
											unitSize.search,
										)}
									>
										Find {unitSize.title} Units near you
									</Button>
								</BasicsCard>
							</Column>
						))}
						{specialUnitSizes.map((unitSize) => (
							<Column
								medium={6}
								xlarge={4}
								key={unitSize.title}
							>
								<BasicsCard header={unitSize.title}>
									<Group className="unit-container">
										<div className="unit-image">
											<Image
												className="unit-icon"
												src={unitSize.image}
												alt=""
											/>
										</div>
										<div className="unit-size-info">
											<Paragraph>
												{unitSize.description}
											</Paragraph>
										</div>
									</Group>
								</BasicsCard>
							</Column>
						))}
					</Row>
				</Container>
			</Section>

			<Section
				className="vehicle-unit-sizes"
				id="vehicle-storage"
			>
				<Container>
					<Group>
						<H2
							className="section-title"
							size="large"
						>
							Vehicle Storage
						</H2>
						<H3 className="section-title">
							To search for vehicle storage, select 'Vehicle -
							Covered' or 'Vehicle - Uncovered' in the Amenities
							drop-down menu.
						</H3>
					</Group>
					<Section>
						<Row>
							<Column
								className="vehicle-storage-column"
								large={6}
							>
								<Group>
									<H4 size="medium">Outdoor Spaces</H4>
									<Paragraph>
										A designated parking spot or an unused
										space on the premises where you can park
										your vehicle. Outdoor spaces can be
										covered or uncovered.
									</Paragraph>
								</Group>
								<Button
									secondary
									fullWidth
									onClick={searchVehicleUnits}
								>
									Search for Vehicle Storage
								</Button>
							</Column>
							<Column
								className="vehicle-storage-column"
								large={6}
							>
								<Group>
									<H4 size="medium">Indoor Spaces</H4>
									<Paragraph>
										Some facilities offer regular storage
										units for your vehicle. If you go with
										this option, remember to find a unit
										with drive-up access. We recommend the
										sizes listed below.
									</Paragraph>
								</Group>
								<Button
									secondary
									fullWidth
									onClick={searchVehicleUnits}
								>
									Search for Vehicle Storage
								</Button>
							</Column>
						</Row>
					</Section>
					<Section>
						<Row>
							{vehicleUnitSizes.map((unitSize) => (
								<Column
									medium={6}
									xlarge={3}
									key={unitSize.sqft}
								>
									<BasicsCard header={unitSize.title}>
										<Group className="unit-container">
											<div className="unit-image">
												<Image
													className={`unit-icon ${unitSize.className}`}
													src={unitSize.image}
													alt={`${unitSize.title} vehicle storage`}
												/>
											</div>
											<dl className="unit-size-info">
												<dt>
													<Text size="small">
														Will Fit:
													</Text>
												</dt>
												<dd>
													<Text>
														{unitSize.willFit}
													</Text>
												</dd>
											</dl>
										</Group>
										<Button
											primary
											fullWidth
											onClick={searchVehcileUnitsBySize(
												unitSize.sqft,
											)}
										>
											Find {unitSize.title} Units near you
										</Button>
									</BasicsCard>
								</Column>
							))}
						</Row>
					</Section>
					<div className="section-title">
						<Group>
							<Paragraph size="small">
								You should not try to fit your vehicle into a
								unit with a door less than ten feet wide.
							</Paragraph>
						</Group>
						<Button
							secondary
							to="/vehicle-storage"
						>
							Click Here to see much more about Vehicle Storage
						</Button>
					</div>
				</Container>
			</Section>

			<Section
				className="unit-amenities"
				id="unit-amenities"
			>
				<Container>
					<Group>
						<H2
							className="section-title"
							size="large"
						>
							Storage Unit Amenities
						</H2>
						<H3 className="section-title">
							A breakdown of the most common amenities offered by
							storage facilities.
						</H3>
					</Group>
					<Row>
						{unitAmenities.map((amenity) => (
							<Column
								medium={6}
								xlarge={4}
								key={amenity.title}
							>
								<div className="unit-amenity">
									<Group>
										<H4
											className="unit-amenity-title"
											size="large"
										>
											<img
												className="unit-amenity-image"
												src={amenity.image}
												alt=""
											/>
											{amenity.title}
										</H4>
										<Paragraph>
											{amenity.description}
										</Paragraph>
									</Group>
									<Button
										secondary
										fullWidth
										onClick={searchUnitsByAmenity(
											amenity.search,
										)}
									>
										Search for {amenity.buttonText} Storage
									</Button>
								</div>
							</Column>
						))}
					</Row>
				</Container>
			</Section>

			<Section
				className="moving-in"
				id="moving-in"
			>
				<Container>
					<H2
						className="section-title"
						size="large"
					>
						Moving In
					</H2>
					<div className="moving-content">
						<div className="moving-text">
							<Group>
								<Paragraph>
									Making a reservation on SelfStorage.com
									ensures that the unit will be available when
									you’re ready to move in.
								</Paragraph>
								<Paragraph>
									On move-in day, simply show up at the
									facility with your confirmation email from
									SelfStorage.com (pulled up on your phone or
									printed out) and let them know that you’re a
									SelfStorage.com customer.{' '}
									<strong>
										Please bring a photo ID and a form of
										payment.{' '}
									</strong>{' '}
									You should also bring a copy of your
									homeowners insurance or renters insurance,
									which may save you from paying additional
									insurance fees.
								</Paragraph>
								<Paragraph>
									Depending on whether the facility charges at
									the first of every month or on your move-in
									anniversary date, you may need to pay a
									pro-rated amount for your first month. Other
									move-in fees may include:
								</Paragraph>
							</Group>
							<H3 size="medium">Additional Fees</H3>
							<Paragraph>
								<strong>Insurance:</strong> Most facilities
								require proof of renters or homeowners insurance
								when you sign your paperwork and move in. You
								may also have the option to purchase insurance
								provided at the facility.
							</Paragraph>
							<Paragraph>
								<strong>Administrative fee:</strong> The admin
								fee is a common storage industry fee that covers
								the cost of signing up a new tenant.
							</Paragraph>
							<Paragraph>
								<strong>Security deposit:</strong> Some
								facilities will charge a security deposit
								upfront. This works in much the same way as a
								security deposit for an apartment rental.
							</Paragraph>
						</div>
						<div className="moving-image">
							<Image
								src={movingInImage}
								alt="Photo of a smiling man carrying a moving box"
							/>
						</div>
					</div>
				</Container>
			</Section>

			<Section
				className="moving-out"
				id="moving-out"
			>
				<Container>
					<H2
						className="section-title"
						size="large"
					>
						Moving Out
					</H2>
					<div className="moving-content">
						<div className="moving-text">
							<Paragraph>
								Please direct all move-out inquiries to your
								facility when you plan to move out. While
								storage leases are typically month-to-month, you
								will be required to give advanced notice of your
								move-out. Additionally, most facilities will not
								prorate your rent when moving out. Be sure to
								coordinate a move-out date with the facility to
								avoid paying for a full month.
							</Paragraph>
							<Paragraph>
								That’s it! On your scheduled move-out day, empty
								your unit and follow any additional procedures
								requested by the facility.
							</Paragraph>
							<Paragraph>
								Have questions about your storage needs?
								SelfStorage.com’s team can help&mdash;don’t
								hesitate to get in touch with us!
							</Paragraph>
						</div>
						<div className="moving-image">
							<Image
								src={movingOutImage}
								alt="Photo of the interior of a self-storage unit"
							/>
						</div>
					</div>
				</Container>
			</Section>
		</Page>
	);
}

StorageBasicsPage.propTypes = {
	canonical: PropTypes.string.isRequired,
	handleSearch: PropTypes.func.isRequired,
	onHideSearchModal: PropTypes.func.isRequired,
	searchModalShowing: PropTypes.bool.isRequired,
	searchUnitsBySize: PropTypes.func.isRequired,
	searchUnitsByAmenity: PropTypes.func.isRequired,
	searchVehicleUnits: PropTypes.func.isRequired,
	searchVehcileUnitsBySize: PropTypes.func.isRequired,
};

export default enhance(StorageBasicsPage);
